export const CHECKOUT_URL = 'https://menofive-en.impultienda.ar/checkout/'

export const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/rPqQ8GGLQupMBEjViTAMo4/MENO5-EN.png'
export const AUTHOR_IMAGE = 'https://cdn.magicpatterns.com/uploads/sxbWXGr7QY5QGw8rsLrKcE/Dra_Karito_real.png'

export const stats = [
  { value: '+300', label: 'Decision algorithms & clinical tools' },
  { value: '+2,000', label: 'Physicians served' },
  { value: '+1,500', label: 'Hours spent searching through guidelines' },
  { value: '99%', label: 'Report greater confidence with a structured process' },
]

export const beforeItems = [
  'You’re unsure where to begin with a new menopause patient.',
  'You spend too much time reviewing different resources.',
  'You’re not always sure which information actually changes the clinical pathway.',
  'You jump between guidelines looking for one specific answer.',
  'Every patient seems to require a completely different approach.',
  'You leave some visits wondering whether you missed an important consideration.',
]

export const afterItems = [
  'You follow a structured process from the start.',
  'You identify the information you need to review.',
  'You move through a clear sequence of clinical decision points.',
  'You use visual algorithms instead of searching through pages.',
  'You follow the same framework across different patient scenarios.',
  'You finish with a structured approach to follow-up.',
]

export const familiarItems = [
  {
    emoji: '😰',
    text: 'A woman with significant menopausal symptoms sits in front of you — and you know there are several factors you need to evaluate before deciding how to proceed.',
  },
  {
    emoji: '⏱️',
    text: 'You spend too much time going back and forth between guidelines, articles, and notes before feeling ready to make a decision.',
  },
  {
    emoji: '🤷',
    text: 'You’re unsure which clinical information actually matters for the specific situation in front of you.',
  },
  {
    emoji: '💊',
    text: 'You know there are multiple treatment options, but organizing the relevant variables quickly can be challenging.',
  },
  {
    emoji: '😓',
    text: 'A patient asks about risks and benefits and you want a clear, professional framework for discussing them.',
  },
  {
    emoji: '😩',
    text: 'Every new perimenopause or menopause patient feels like you’re starting from scratch.',
  },
]

export type Guide = {
  number: string
  title: string
  hook: string
  description?: string
  items: string[]
  note?: string
}

export const guides: Guide[] = [
  {
    number: 'GUIDE I',
    title: 'The First 5 Minutes',
    hook: 'Know where to start with every new menopause patient.',
    description:
      'A structured framework for organizing the initial assessment and identifying the information you need before moving forward.',
    items: [
      'Initial assessment framework',
      'Key clinical questions',
      'Focused history checklist',
      'Symptom assessment',
      'Differential considerations',
      'Initial consultation checklist',
      'Orientation algorithm',
      'Practical cases',
    ],
  },
  {
    number: 'GUIDE II',
    title: 'The Safety Filter',
    hook: 'Know what to review before moving forward.',
    description:
      'A visual framework for organizing relevant cardiovascular, thrombotic, oncologic, and other clinical considerations.',
    items: [
      'Risk-factor matrix',
      'Safety checklist',
      'Risk-stratification algorithms',
      'Clinical review points',
      'Special considerations',
      'Key questions',
      'Practical cases',
    ],
  },
  {
    number: 'GUIDE III',
    title: 'The Treatment Decision Map',
    hook: 'Stop looking at treatment options in isolation.',
    description:
      'Organize the key variables involved in individualized menopause management decisions.',
    items: [
      'Treatment decision tree',
      'Option comparison matrix',
      'Individualization framework',
      'Route considerations',
      'Treatment variables',
      'Reassessment framework',
      'Practical cases',
      'Quick-reference sheets',
    ],
    note: 'Educational material based on current evidence and intended to support—not replace—individual clinical judgment.',
  },
  {
    number: 'GUIDE IV',
    title: 'Optimize the Follow-Up',
    hook: 'The first treatment decision is only the beginning.',
    description:
      'A structured framework for monitoring, reassessing, and organizing follow-up.',
    items: [
      'Follow-up decision map',
      'Monitoring checklist',
      'Reassessment framework',
      'Common follow-up scenarios',
      'Documentation tools',
      'Progress tracking sheet',
      'Follow-up cases',
      'Consultation templates',
    ],
  },
  {
    number: 'GUIDE V',
    title: 'The Complex Case System',
    hook: 'When the patient doesn’t fit the typical scenario, know how to structure the review.',
    description:
      'A collection of frameworks and cases for situations requiring additional clinical consideration.',
    items: [
      'Complex patient scenarios',
      'Relevant comorbidities',
      'Special considerations',
      'Review algorithms',
      'Nonhormonal options framework',
      'Decision matrices',
      'Complex cases',
      'Clinical reference tools',
    ],
  },
  {
    number: 'GUIDE VI',
    title: 'The 5-Min Consultation Toolkit',
    hook: 'Turn the entire method into practical tools you can actually use.',
    items: [
      'Master 5-Min Checklist',
      'First-visit checklist',
      'Follow-up checklist',
      'Decision sheets',
      'Clinical matrices',
      'Consultation templates',
      'Protocol frameworks',
      'Communication scripts',
      'Quick-reference pages',
      'Progress tracking tools',
    ],
  },
]

export const immediateAccess = [
  {
    emoji: '🧭',
    title: 'The 5-Min Master Map',
    text: 'The entire method on one visual page: ORIENT → ASSESS → FILTER → DECIDE → FOLLOW UP',
  },
  {
    emoji: '📋',
    title: '30+ Practical Clinical Tools',
    text: 'Checklists, decision sheets, matrices, reference pages, and consultation resources.',
  },
  {
    emoji: '🧠',
    title: 'Decision Algorithms',
    text: 'Visual pathways designed to help organize complex clinical information into structured decision points.',
  },
  {
    emoji: '📊',
    title: 'Clinical Matrices',
    text: 'Simple frameworks for organizing the variables you need to review before moving forward.',
  },
  {
    emoji: '🩺',
    title: 'Practical Clinical Cases',
    text: 'Realistic scenarios designed to help you practice applying the 5-Min framework.',
  },
]

export type Bonus = {
  tag: string
  title: string
  value: string
  image: string
  description: string
  listLabel?: string
  items?: string[]
  closing?: string
}

export const bonuses: Bonus[] = [
  {
    tag: 'BONUS #1',
    title: '50 Menopause Cases — Solved',
    value: '$47',
    image: 'https://cdn.magicpatterns.com/uploads/1MgmP9vsM33TxzNaTLHZVw/BONUS_1_MENO5-EN.png',
    description:
      'A collection of practical clinical scenarios designed to help you apply the 5-MIN MENOPAUSE™ framework.',
    listLabel: 'Every case follows:',
    items: [
      'Scenario',
      'Clinical Information',
      'Key Question',
      'Decision Path',
      'Rationale',
      'Follow-Up',
    ],
    closing:
      'A practical way to train your decision process before encountering similar situations in practice.',
  },
  {
    tag: 'BONUS #2',
    title: 'Menopause Quick Reference™',
    value: '$37',
    image: 'https://cdn.magicpatterns.com/uploads/bGGQVMkBApNezJ4hrkWLJk/BONUS_2_MENO5-EN.png',
    description:
      'The most useful components of the 5-Min Method condensed into quick-reference pages. Instead of searching through the entire system, find the relevant framework at a glance.',
    listLabel: 'Includes:',
    items: ['Decision maps', 'Tables', 'Checklists', 'Algorithms', 'Quick-reference sheets'],
  },
  {
    tag: 'BONUS #3',
    title: 'Difficult Conversations™',
    value: '$47',
    image: 'https://cdn.magicpatterns.com/uploads/nvC6B7KURzXdmT5RKvMR3z/BONUS_3_MENO5-EN.png',
    description:
      'Communication frameworks for navigating common questions and challenging conversations around menopause care.',
    listLabel: 'Includes frameworks for discussing:',
    items: [
      'Risks',
      'Benefits',
      'Expectations',
      'Concerns',
      'Adherence',
      'Follow-up',
      'Shared decision-making',
    ],
    closing: 'The goal isn’t to memorize answers. It’s to have a structure for the conversation.',
  },
  {
    tag: 'BONUS #4',
    title: 'Menopause Error Check™',
    value: '$37',
    image: 'https://cdn.magicpatterns.com/uploads/b85UxyEcwpxcAgw6iBSR37/BONUS_4_MENO5-EN.png',
    description:
      'A collection of common process pitfalls and review points designed to help you identify areas that may deserve additional attention.',
    listLabel: 'Includes:',
    items: [
      'Common workflow errors',
      'Questions worth revisiting',
      'Review checkpoints',
      'Self-audit checklists',
      'Clinical scenarios',
    ],
  },
  {
    tag: 'BONUS #5',
    title: '30 Professional Menopause Checklists',
    value: '$29',
    image: 'https://cdn.magicpatterns.com/uploads/6edUPAogw2eiYFyA7NYfFx/BONUS_5_MENO5-EN.png',
    description:
      'A complete collection of checklists for different stages of the menopause consultation.',
    listLabel: 'Includes:',
    items: [
      'Initial visit',
      'Assessment',
      'Safety review',
      'Decision-making',
      'Follow-up',
      'Reassessment',
      'Documentation',
      'Communication',
    ],
  },
]

export const guaranteeItems = [
  'Open the 6 guides.',
  'Review the decision maps.',
  'Use the checklists.',
  'Work through the cases.',
  'Explore the bonus materials.',
]

export const testimonials = [
  {
    name: 'Dr. Maria L.',
    flag: '🇨🇴',
    country: 'Colombia',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/c8bd1bd7-e3cd-456e-8ab5-5724bd95cfbe.jpg',
    quote:
      'I used to spend so much time reviewing different resources before a menopause consultation. Having a structured sequence makes the process much easier to organize.',
  },
  {
    name: 'Dr. Robert M.',
    flag: '🇺🇸',
    country: 'United States',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/ff228218-cd84-4e09-9bdb-d149a8fc00c7.jpg',
    quote:
      'What I liked most is that it doesn’t feel like another textbook. It’s designed around the decisions you actually have to organize during a visit.',
  },
  {
    name: 'Dr. Andrea P.',
    flag: '🇦🇷',
    country: 'Argentina',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/cda8764e-b340-4e4d-b21a-6acb1056881b.jpg',
    quote:
      'The decision maps and checklists make the consultation much more structured. I don’t have to rely on memory for every step.',
  },
  {
    name: 'Dr. Julia R.',
    flag: '🇨🇦',
    country: 'Canada',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/bd063d10-b95c-4122-8dc9-2109177651f5.jpg',
    quote:
      'I thought I was getting another set of PDFs. Instead, I got a framework I can actually use when preparing for a menopause visit.',
  },
  {
    name: 'Dr. Camila S.',
    flag: '🇺🇸',
    country: 'United States',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/2cf25f5d-fad4-4338-85d2-ef5b8f7343d0.jpg',
    quote:
      'The case library is especially useful because it shows how to apply the framework to different scenarios.',
  },
]

export const methodSteps = ['Orient', 'Assess', 'Filter', 'Decide', 'Follow Up']

export const faqs = [
  {
    q: 'How will I receive the material?',
    a: 'Immediately after your payment is confirmed, you’ll receive access instructions to download the 6 guides and 5 bonuses digitally.',
  },
  {
    q: 'Is the content suitable for physicians in the United States?',
    a: 'The framework is designed as an educational resource for healthcare professionals. Clinical decisions should always take into account current evidence, applicable professional standards, local regulations, patient-specific factors, and individual clinical judgment.',
  },
  {
    q: 'Is this only for OB/GYNs or menopause specialists?',
    a: 'No. The material is designed for healthcare professionals who evaluate and manage patients experiencing perimenopause and menopause within their professional scope of practice.',
  },
  {
    q: 'Do I need to be a menopause specialist?',
    a: 'No. The system is designed to organize the clinical information and decision points relevant to menopause care. It is an educational resource and does not replace appropriate medical training or specialty consultation when indicated.',
  },
  {
    q: 'Is this a course?',
    a: 'No. 5-MIN MENOPAUSE™ is designed as a clinical decision and consultation system, rather than a long video course.',
  },
  {
    q: 'Are the files downloadable?',
    a: 'Yes. The materials are delivered digitally and can be accessed from a computer, tablet, or phone.',
  },
  {
    q: 'Is this a one-time payment?',
    a: 'Yes. You pay $27 once and receive lifetime access to the materials included in this offer.',
  },
  {
    q: 'Is the material updated?',
    a: 'The product should display its evidence/update date and source list. Clinical guidance evolves, so always verify recommendations against current authoritative guidelines before applying them in practice.',
  },
  {
    q: 'What if I don’t like it?',
    a: 'You are covered by the 7-day guarantee. Review the materials and, if you determine that the system isn’t valuable for your practice, contact us within the applicable guarantee period to request your refund.',
  },
]

export const purchaseNotifications = [
  { name: 'Valentina G.', country: 'United States', flag: '🇺🇸' },
  { name: 'Dra. Lucía M.', country: 'Spain', flag: '🇪🇸' },
  { name: 'Dr. Andrés R.', country: 'Mexico', flag: '🇲🇽' },
  { name: 'Dra. Camila T.', country: 'Chile', flag: '🇨🇱' },
  { name: 'Dr. Javier P.', country: 'Argentina', flag: '🇦🇷' },
  { name: 'Dra. Mariana S.', country: 'Colombia', flag: '🇨🇴' },
  { name: 'Dr. Ricardo L.', country: 'Peru', flag: '🇵🇪' },
  { name: 'Dra. Paola V.', country: 'Ecuador', flag: '🇪🇨' },
  { name: 'Dr. Sebastián C.', country: 'Uruguay', flag: '🇺🇾' },
  { name: 'Dra. Isabel N.', country: 'Costa Rica', flag: '🇨🇷' },
  { name: 'Dr. Tomás H.', country: 'Panama', flag: '🇵🇦' },
  { name: 'Dra. Daniela F.', country: 'Dominican Republic', flag: '🇩🇴' },
]
